import { lazy, Suspense, useEffect, useMemo } from 'react'
import { AppShell } from './app/AppShell'
import { NotFoundPage } from './components/NotFoundPage'
import { normalizeFeedChannel } from './domain/helpers'
import { EmailNextStepPage } from './features/auth/EmailNextStepPage'
import { LoginPage } from './features/auth/LoginPage'
import { RecoveryPage } from './features/auth/RecoveryPage'
import { RegisterPage } from './features/auth/RegisterPage'
import { TicketOnboardingPage } from './features/auth/TicketOnboardingPage'
import { WelcomePage } from './features/auth/WelcomePage'
import { HomePage } from './features/home/HomePage'
import { citySlug } from './features/home/fanExperience'
import { ShowDetailPage } from './features/tour/ShowDetailPage'
import { TourPage } from './features/tour/TourPage'
import { initializeQaSurface, updateQaSurface } from './lib/qa'
import { routeLabel, safeReturnTo } from './routing/routeTable'
import { useBrowserRouter } from './routing/useBrowserRouter'
import { useAppActions, useAppState } from './state/AppProvider'

const publicAccountRoutes = new Set(['welcome', 'register', 'login', 'recovery'])

const CheckInPage = lazy(() => import('./features/checkin/CheckInPage').then((module) => ({ default: module.CheckInPage })))
const ContentDetailPage = lazy(() => import('./features/content/ContentDetailPage').then((module) => ({ default: module.ContentDetailPage })))
const ContentPage = lazy(() => import('./features/content/ContentPage').then((module) => ({ default: module.ContentPage })))
const FeedPage = lazy(() => import('./features/feed/FeedPage').then((module) => ({ default: module.FeedPage })))
const ItineraryPage = lazy(() => import('./features/itinerary/ItineraryPage').then((module) => ({ default: module.ItineraryPage })))
const NotificationsPage = lazy(() => import('./features/notifications/NotificationsPage').then((module) => ({ default: module.NotificationsPage })))
const PassportPage = lazy(() => import('./features/passport/PassportPage').then((module) => ({ default: module.PassportPage })))
const ProfilePage = lazy(() => import('./features/profile/ProfilePage').then((module) => ({ default: module.ProfilePage })))
const AccessibilitySettingsPage = lazy(() => import('./features/settings/AccessibilitySettingsPage').then((module) => ({ default: module.AccessibilitySettingsPage })))
const NotificationSettingsPage = lazy(() => import('./features/settings/NotificationSettingsPage').then((module) => ({ default: module.NotificationSettingsPage })))
const PrivacySettingsPage = lazy(() => import('./features/settings/PrivacySettingsPage').then((module) => ({ default: module.PrivacySettingsPage })))
const SettingsPage = lazy(() => import('./features/settings/SettingsPage').then((module) => ({ default: module.SettingsPage })))
const SupportPage = lazy(() => import('./features/support/SupportPage').then((module) => ({ default: module.SupportPage })))
const TicketConnectPage = lazy(() => import('./features/tickets/TicketConnectPage').then((module) => ({ default: module.TicketConnectPage })))
const TicketDetailPage = lazy(() => import('./features/tickets/TicketDetailPage').then((module) => ({ default: module.TicketDetailPage })))
const TicketsPage = lazy(() => import('./features/tickets/TicketsPage').then((module) => ({ default: module.TicketsPage })))

function RouteLoading() {
  return (
    <section aria-busy="true" aria-live="polite" className="boot-screen route-loading">
      <span aria-hidden="true" />
      <p>Loading this stop…</p>
    </section>
  )
}

export default function App() {
  const state = useAppState()
  const actions = useAppActions()
  const { location, navigate } = useBrowserRouter()
  const authenticated = Boolean(state.user && state.session)

  useEffect(() => {
    const updateOnlineState = () => actions.setOnline(navigator.onLine)
    updateOnlineState()
    window.addEventListener('online', updateOnlineState)
    window.addEventListener('offline', updateOnlineState)
    return () => {
      window.removeEventListener('online', updateOnlineState)
      window.removeEventListener('offline', updateOnlineState)
    }
  }, [actions])

  useEffect(() => {
    if (!state.bootstrapped) return
    const path = window.location.pathname
    if (path === '/city' || path === '/city/') {
      navigate('/home', { replace: true })
      return
    }
    if (path === '/links' || path === '/links/') {
      navigate(authenticated ? '/profile#official-links' : '/content/content-music-links', { replace: true })
      return
    }
    if (location.id === 'feed' && location.params.channel === 'energy') {
      const canonicalPath = location.pathname.replace(/\/energy(?=\/|$)/, '/scene')
      navigate(`${canonicalPath}${location.search}${window.location.hash}`, { replace: true })
      return
    }
    if (location.id === 'root') {
      navigate(authenticated ? '/home' : '/welcome', { replace: true })
      return
    }
    if (location.protected && !authenticated) {
      const next = encodeURIComponent(`${location.pathname}${location.search}${window.location.hash}`)
      navigate(`/auth/sign-in?next=${next}`, { replace: true })
      return
    }
    if (authenticated && publicAccountRoutes.has(location.id)) {
      const next = safeReturnTo(new URLSearchParams(location.search).get('next'))
      navigate(state.emailNextStepPending ? '/auth/email-next-step' : next, { replace: true })
    }
  }, [authenticated, location, navigate, state.bootstrapped, state.emailNextStepPending])

  useEffect(() => {
    document.title = `${routeLabel(location.id)} — The Paradox`
    initializeQaSurface(location.pathname, 'caution-roster-full-app')
    const frame = window.requestAnimationFrame(() => {
      updateQaSurface({
        passportStampedStops: state.passportStamps.map((stamp) => stamp.showId),
        sponsorInstances: document.querySelectorAll('[data-presenter-lockup]').length,
        externalRuntimeRequests: [],
      })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [location, state.passportStamps])

  useEffect(() => {
    document.documentElement.dataset.reduceMotion = state.preferences.reducedMotion ? 'true' : 'false'
    document.documentElement.dataset.highContrast = state.preferences.highContrast ? 'true' : 'false'
  }, [state.preferences.highContrast, state.preferences.reducedMotion])

  const selectedShow = state.shows.find((show) => show.id === state.selectedShowId) ?? state.shows[0]
  const selectedCity = selectedShow?.city ?? ''
  const openedShow = location.id === 'show-detail'
    ? state.shows.find((show) => show.id === location.params.showId)
    : undefined
  const shellCityLabel = openedShow && openedShow.id !== selectedShow?.id
    ? `Home city · ${selectedCity}`
    : selectedCity
  const communityPath = selectedShow
    ? `/community/${citySlug(selectedShow)}/${state.preferences.defaultChannel}`
    : '/community/dallas-tx/main'
  const unreadNotifications = state.notifications.filter((notification) => !notification.readAt).length

  const page = useMemo(() => {
    if (!state.bootstrapped) {
      return (
        <section aria-busy="true" aria-live="polite" className="boot-screen">
          <span aria-hidden="true" />
          <p>Loading your route…</p>
        </section>
      )
    }

    if (location.protected && !authenticated) {
      return <section className="boot-screen"><p>Taking you to sign in…</p></section>
    }

    switch (location.id) {
      case 'welcome': return <WelcomePage navigate={navigate} />
      case 'register': return <RegisterPage navigate={navigate} />
      case 'login': return <LoginPage navigate={navigate} />
      case 'recovery': return <RecoveryPage navigate={navigate} />
      case 'email-next-step': return <EmailNextStepPage navigate={navigate} />
      case 'onboarding-city': return <TourPage authenticated={authenticated} navigate={navigate} />
      case 'onboarding-ticket': return <TicketOnboardingPage navigate={navigate} />
      case 'home': return <HomePage navigate={navigate} />
      case 'tour': return <TourPage authenticated={authenticated} navigate={navigate} />
      case 'show-detail': return <ShowDetailPage authenticated={authenticated} navigate={navigate} showId={location.params.showId ?? ''} />
      case 'feed': {
        const channel = normalizeFeedChannel(location.params.channel ?? state.preferences.defaultChannel)
        const slug = location.params.citySlug ?? selectedCity.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '-')
        return <FeedPage channel={channel} citySlug={slug} navigate={navigate} postId={location.params.postId} />
      }
      case 'tickets': return <TicketsPage navigate={navigate} />
      case 'ticket-connect': return <TicketConnectPage navigate={navigate} />
      case 'ticket-detail': return <TicketDetailPage navigate={navigate} ticketId={location.params.ticketId ?? ''} />
      case 'itinerary': return <ItineraryPage navigate={navigate} />
      case 'check-in': return <CheckInPage navigate={navigate} showId={location.params.showId ?? ''} />
      case 'passport': return <PassportPage navigate={navigate} />
      case 'notifications': return <NotificationsPage navigate={navigate} />
      case 'content': return <ContentPage navigate={navigate} />
      case 'content-detail': return <ContentDetailPage authenticated={authenticated} contentId={location.params.contentId ?? ''} navigate={navigate} />
      case 'profile': return <ProfilePage navigate={navigate} />
      case 'settings': return <SettingsPage navigate={navigate} />
      case 'settings-accessibility': return <AccessibilitySettingsPage navigate={navigate} />
      case 'settings-privacy': return <PrivacySettingsPage navigate={navigate} />
      case 'settings-notifications': return <NotificationSettingsPage navigate={navigate} />
      case 'support': return <SupportPage navigate={navigate} />
      default: return <NotFoundPage authenticated={authenticated} navigate={navigate} />
    }
  }, [authenticated, location, navigate, selectedCity, state.bootstrapped, state.preferences.defaultChannel])

  return (
    <AppShell
      activeRoute={location.id}
      authenticated={authenticated}
      communityPath={communityPath}
      notificationCount={unreadNotifications}
      online={state.online}
      onNavigate={navigate}
      selectedCity={shellCityLabel}
    >
      <Suspense fallback={<RouteLoading />}>
        {page}
      </Suspense>
    </AppShell>
  )
}
